/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, IPayLoad, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IRefineItemData, isRangeType } from './common';

/**
 * IChoiceSummaryClickNotification Interface.
 */
export interface IChoiceSummaryClickNotification {
    selectedRefineItem: IRefineItemData | undefined;
    shouldClearAll: boolean;
    nextItemToFocus: HTMLElement | undefined;
}

/**
 * IChoiceSummaryComponent Interface.
 */
export interface IChoiceSummaryComponent extends IComponent<IChoiceSummaryProps> {}

/**
 * IChoiceSummaryData interface.
 */
export interface IChoiceSummaryData {
    selectedChoices: IRefineItemData[];
}

/**
 * IChoiceSummaryProps Interface.
 */
export interface IChoiceSummaryProps extends IComponentProps<IChoiceSummaryData> {
    className?: string;
    clearAllText?: string;
    label?: string;
    choiceAriaLabel?: string;
    choiceFormat?: string;
    closeAriaLabel?: string;
    telemetryContent?: ITelemetryContent;
    choiceRangeValueFormat?: string;
    formatPrice(amount: string | undefined, currencyCode: string | undefined): string;
    onChoiceItemClicked(notification: IChoiceSummaryClickNotification): void;
    urlBuilder(isClearAll: boolean, selectedRefiner?: IRefineItemData): string;
}

/**
 * Choice summary component.
 * @param props - IChoiceSummaryProps.
 * @returns - Renders the choice summary.
 */
const ChoiceSummaryFunctionalComponent: React.FC<IChoiceSummaryProps> = (props: IChoiceSummaryProps) => {
    const {
        clearAllText,
        label,
        className,
        choiceAriaLabel,
        closeAriaLabel,
        data: { selectedChoices },
        formatPrice,
        choiceFormat,
        choiceRangeValueFormat,
        onChoiceItemClicked,
        urlBuilder
    } = props;

    const payLoad: IPayLoad = getPayloadObject('click', props.telemetryContent!, '');
    const closeButtonGlyph: string = 'msi-close-btn';

    /**
     * Get choice summary name.
     * @param activeRefinerItem - Active refine Item data.
     * @returns - Returns the tag name.
     */
    const getTagName = (activeRefinerItem: IRefineItemData): string => {
        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- Required for string.  */
        const overallFormat = choiceFormat || '{1}';
        const rangeFormat = choiceRangeValueFormat;
        const refinerName = activeRefinerItem.name;

        let refinerValueName: string;
        if (isRangeType(activeRefinerItem.dataTypeValue)) {
            refinerValueName =
                rangeFormat
                    ?.replace('{0}', formatPrice(activeRefinerItem.minValue, activeRefinerItem.unitText))
                    .replace('{1}', formatPrice(activeRefinerItem.maxValue, activeRefinerItem.unitText)) || '';
        } else {
            refinerValueName = activeRefinerItem.label || activeRefinerItem.minValue || '';
        }

        return overallFormat.replace('{0}', refinerName).replace('{1}', refinerValueName);
        /* eslint-enable @typescript-eslint/prefer-nullish-coalescing -- Required for string.  */
    };

    const onClick = React.useCallback(
        (selectedRefineItem?: IRefineItemData) => (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
            event.preventDefault();
            event.stopPropagation();

            const targetChoiceItem = event.currentTarget as HTMLElement;
            const shouldClearAll: boolean = targetChoiceItem.getAttribute('class')!.includes('choice-summary__clear-all');

            onChoiceItemClicked({
                selectedRefineItem,
                shouldClearAll,
                nextItemToFocus: targetChoiceItem.nextSibling as HTMLElement
            });
        },
        [onChoiceItemClicked]
    );

    payLoad.contentAction.etext = clearAllText;
    const clearAllAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);

    return (
        <div className='msc-choice-summary'>
            {ArrayExtensions.hasElements(selectedChoices) && label && <span className='msc-choice-summary__label'>{label}</span>}
            <ul className={classnames(className, 'msc-choice-summary__list', 'list-unstyled')}>
                {ArrayExtensions.hasElements(selectedChoices) &&
                    selectedChoices.map((choiceItem, index) => {
                        const choiceItemName = getTagName(choiceItem);
                        payLoad.contentAction.etext = choiceItemName;
                        const choiceItemAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);
                        return (
                            <li className='msc-choice-summary__list-item' key={index}>
                                <a
                                    href={urlBuilder(false, choiceItem)}
                                    key={choiceItem.refinerItemId}
                                    onClick={onClick(choiceItem)}
                                    className='msc-choice-summary__item'
                                    aria-label={`${choiceItemName} ${choiceAriaLabel || ''}`}
                                    {...choiceItemAttribute}
                                    role='button'
                                >
                                    {choiceItemName}
                                    <span
                                        className={`${closeButtonGlyph} msc-choice-summary__glyph`}
                                        role='button'
                                        aria-label={closeAriaLabel}
                                    />
                                </a>
                            </li>
                        );
                    })}
            </ul>
            {ArrayExtensions.hasElements(selectedChoices) && clearAllText && (
                <a
                    href={urlBuilder(true)}
                    className='msc-choice-summary__clear-all'
                    onClick={onClick()}
                    aria-label={closeAriaLabel}
                    {...clearAllAttributes}
                >
                    {clearAllText}
                </a>
            )}
        </div>
    );
};

/**
 * Choice Summary Component.
 */
export const ChoiceSummaryComponent: React.FunctionComponent<IChoiceSummaryProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    IChoiceSummaryComponent
>('Choice-Summary', { component: ChoiceSummaryFunctionalComponent });
